import { Feature, Map as OLMap } from 'ol';
import AbstractLayer from 'controllers/AbstractLayer';
import { LayerManager } from 'models';
import { makeAPICall } from 'models/api';
import { Geometry } from 'ol/geom';
import { EsriJSON as esriJSON } from 'ol/format';
import { LegendModel } from './legend';
/**
 * Meant to define the relevant details of a Web Map Service (WMS)
 * To avoid rewriting the wheel too much, best to implement from OpenLayers (import 'ol') types or
 * have types that extend them for our own specific use cases.
 *
 * For a general WMS Spec see this url:
 * https://docs.geoserver.org/stable/en/user/services/wms/reference.html
 *
 */
export declare namespace Esri {
    export type GetLegendGraphic = Pick<Esri.Params, 'f'>;

    export interface Params {
        /* Shared between most request Types */
        f?: 'pjson' | 'application/json';
        /* Specific to both GetFeatureInfo and GetMap */
        where?: string;
        inSR?: string;
        outSRS?: string;
        geometry?: object;
        geometryType?: 'esriGeometryEnvelope' | 'esriGeometryPolygon';
        spatialRel?: 'esriSpatialRelIntersects';
    }
}

const makeGetLegendQueryDefaults: Pick<Esri.GetLegendGraphic, 'f'> = {
    f: 'pjson',
};

export function makeGetLegendQuery(
    params?: Omit<
        Esri.GetLegendGraphic,
        keyof typeof makeGetLegendQueryDefaults
    > &
        Partial<typeof makeGetLegendQueryDefaults>,
): Esri.GetLegendGraphic {
    return {
        ...makeGetLegendQueryDefaults,
        ...params,
    };
}

const mapToLegend = (
    data: LegendModel.EsriResponseDetails,
): LegendModel.Legend => {
    const rules: LegendModel.Rule[] = [];
    for (const x of data.legend) {
        rules.push({
            title: x.label,
            symbolizers: [
                {
                    EsriGraphic: {
                        contentType: x.contentType,
                        imageData: `${x.imageData}`,
                    },
                },
            ],
        });
    }
    return {
        layerName: data.layerName,
        title: data.layerName,
        rules: rules,
    };
};

// Calculate buffer distance for 5 pixel
const getBufferDistance = (resolution: number) => {
    return `${resolution * 5}`;
};

const getScaleForResolution = (resolution: number, mpu: number) => {
    const dpi = 25.4 / 0.28;
    const inchesPerMeter = 39.37;
    return resolution * mpu * inchesPerMeter * dpi;
};

const convertParameters = (request: any, skipEncoding?: boolean) =>
    Object.keys(request)
        .map(
            (key) =>
                `${key}=${
                    skipEncoding
                        ? `${(request as any)[key]}`
                        : encodeURIComponent((request as any)[key])
                }`,
        )
        .join('&');

const getConfigValue = (configs: any[], key: any, defaultValue: any = null) => {
    return (
        configs
            .map((l) => l.config[key])
            .find((value) => value !== null && value !== undefined) ??
        defaultValue
    );
};

export const forceGetLegend = makeAPICall<
    LegendModel.Legend,
    { config: LayerManager.LayerConfig },
    LegendModel.EsriResponse
>(
    ({ config }) => {
        const query = makeGetLegendQuery({ f: 'pjson' });
        return {
            url: `${config.url}/legend?${convertParameters(query)}`,
            skipAuth: false,
            config,
        };
    },
    (root, layerConfig) => {
        // Log root to inspect its structure
        console.log('Root object:', root, layerConfig);
        return mapToLegend(
            root.layers[layerConfig.config.mapserviceLayerId || 0],
        );
    },
);

interface QueryWMSFeaturesParams {
    layers: AbstractLayer[];
    coords: number[];
    srs?: string;
    date?: Date;
    issueTime?: Date;
    filter?: string;
    map?: OLMap;
}

export const queryFeatures = makeAPICall<
    Feature<Geometry>[],
    QueryWMSFeaturesParams,
    string
>(
    ({ coords, layers, map }) => {
        const layerUrl = getConfigValue(layers, 'url');
        const minScale = getConfigValue(layers, 'minScale', 0);
        const maxScale = getConfigValue(layers, 'maxScale', 0);
        const mapserviceLayerId = getConfigValue(
            layers,
            'mapserviceLayerId',
            null,
        );

        if (!layerUrl || !map || mapserviceLayerId == null) {
            throw new Error('Invalid layerSource or Map Object');
        }

        const resolution = map.getView().getResolution() || 10;
        const bufferDistance = getBufferDistance(resolution);
        const projection = map.getView().getProjection();
        const epsgCode = projection.getCode();
        const mpu = projection.getMetersPerUnit();
        const mapScale = mpu ? getScaleForResolution(resolution, mpu) : null;

        // Default where clause to match nothing and enable this based on the scale.
        let whereClause = '1 <> 1';
        if (mapScale == null || minScale == 0) {
            whereClause = '1 = 1';
        } else if (mapScale <= minScale && mapScale >= maxScale) {
            whereClause = '1 = 1';
        }

        // Extract the number part
        const srs = epsgCode?.match(/\d+/)?.[0] ?? '4326'; // Default to '4326' if not found

        // Perpare the extent of identify
        const [x, y] = coords;
        const geometry = JSON.stringify({
            xmin: x - 0.05,
            ymin: y - 0.05,
            xmax: x + 0.05,
            ymax: y + 0.05,
            spatialReference: { wkid: srs },
        });

        const params = {
            F: 'pjson',
            WHERE: whereClause,
            OUTFIELDS: '*',
            DISTANCE: bufferDistance,
            UNITS: 'esriSRUnit_Meter',
            RESULTRECORDCOUNT: '5',
            GEOMETRY: geometry,
            GEOMETRYTYPE: 'esriGeometryEnvelope',
            INSR: srs,
            SPATIALREL: 'esriSpatialRelIntersects',
            OUTSR: srs,
        };

        const queryString = new URLSearchParams(params).toString();
        const url = `${layerUrl}/${mapserviceLayerId}/query?${queryString}`;

        return {
            url,
            dataType: 'text',
        };
    },
    (root) => new esriJSON().readFeatures(root) as Feature<Geometry>[],
);
